import { Link } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { crewPath, editMissionPath, missionPath } from "@/routes"

/**
 * MissionAssignEmptyStates — three discriminated empty states for the
 * assignment surface (UX Spec §6):
 *   - `zero_crew`     — brand has no active memberships.
 *   - `draft_mission` — mission is still a draft; "Activate to assign".
 *   - `all_assigned`  — every eligible creator is already on the mission.
 *
 * Each variant renders its own copy + CTA. Parent picks the variant
 * based on the controller's `draft_state` / `empty_crew_state` /
 * (preview.target_membership_ids.length === 0 && already > 0) flags.
 */
type EmptyVariant = "zero_crew" | "draft_mission" | "all_assigned"

interface MissionAssignEmptyStatesProps {
  variant: EmptyVariant
  missionId: number
  testId?: string
}

// QA Plan / Playwright contract uses singular `empty-state-*` with dashes
// and short suffixes; keep the variant names underscore_case internally
// (Rails-friendly i18n key suffix) but emit the QA-aligned testid.
const TESTID_SUFFIX: Record<EmptyVariant, string> = {
  zero_crew: "zero-crew",
  draft_mission: "draft",
  all_assigned: "all-assigned",
}

export function MissionAssignEmptyStates({
  variant,
  missionId,
  testId = "mission-assign-empty-state",
}: MissionAssignEmptyStatesProps) {
  const { t } = useTranslation()

  const config = useEmptyStateConfig(variant, missionId)
  const suffix = TESTID_SUFFIX[variant]

  return (
    <div
      data-testid={`${testId}-${suffix}`}
      className="flex flex-col items-center gap-3 rounded-lg border-2 border-dashed p-10 text-center"
    >
      <h2 className="text-foreground text-lg font-semibold">
        {t(`missions.assign.empty.${variant}.title`)}
      </h2>
      <p className="text-muted-foreground max-w-md text-sm">
        {t(`missions.assign.empty.${variant}.body`)}
      </p>
      <Button asChild>
        <Link href={config.href} data-testid={`${testId}-${suffix}-cta`}>
          {t(`missions.assign.empty.${variant}.cta`)}
        </Link>
      </Button>
    </div>
  )
}

function useEmptyStateConfig(variant: EmptyVariant, missionId: number) {
  switch (variant) {
    case "zero_crew":
      return { href: crewPath() }
    case "draft_mission":
      return { href: editMissionPath(missionId) }
    case "all_assigned":
      return { href: missionPath(missionId) }
  }
}
